import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useAnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { formatEditedAt } from '@src/utility/dateFormat';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import {
  COVERAGE_TOOLTIP_STYLE,
  CoverageBadgeWithTooltip,
} from './CoverageBadge';
import {
  ButtonRow,
  CollapsibleOriginal,
  CollapsibleValueSection,
  fieldFocusCss,
  InlineTextAction,
  OriginalReference,
  PrimaryButton,
  SecondaryButton,
  SectionLabel,
  Spacer,
} from './translationEditorPrimitives';
import { useOutsideAlerter } from './useClickOutside';
import { useExclusiveTranslationPopover } from './useExclusiveTranslationPopover';

import type { AppliedTerm } from './translationMemoryCards';
import type { AnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import type { CoverageVerdict } from '@src/types.ts/translation';

export interface TranslatedPhraseEditorPopoverProps {
  /** Phrase-leading `<Word>` element. Popover anchors below this rect. */
  anchorElement: HTMLElement;

  /** Source-language text for the phrase (read-only context row). */
  sourceText: string;

  /**
   * PLAT-10644 — human-readable source-language name ("Italian") for the
   * "Original · <language>" section label. Hosts derive it via
   * `formatLanguageCaption(translation.sourceLanguage)`. Omitted → the
   * label falls back to "Original" alone.
   */
  sourceLanguageLabel?: string;

  /**
   * Machine-generated original translation. Derived by the caller from:
   *   1. `phrase.originalTranslatedValue` if captured by the backend, or
   *   2. `phrase.translatedValue` when `!phrase.isUserEdited` (current
   *      stored value IS the machine original in that case).
   *
   * When undefined we cannot determine the machine original — Revert
   * disables and the "Previous translation" row hides. That's the
   * `isUserEdited && !originalTranslatedValue` case, which only resolves
   * after the backend adds `originalTranslatedValue` capture to the
   * Generate / Regenerate pipeline.
   */
  originalValue?: string;

  /** Current value to seed the textarea with (pending edit or stored value). */
  currentValue: string;

  /** Commits the current input value. Fires on Save click or Enter. */
  onSave: (value: string) => void;

  /** Closes without committing. Fires on Cancel, Escape, or click-outside. */
  onCancel: () => void;

  /**
   * Whether the phrase has an applied edit to revert — drives the Revert
   * button's enabled state. The host computes it as the shared `isEdited`
   * predicate (applied translation differs from the machine baseline), so
   * Revert reflects the APPLIED change, not the in-progress draft.
   */
  canRevert: boolean;

  /**
   * Reverts the applied change to the machine original. For a phrase-record
   * edit the host resets `translatedValue` + clears `isUserEdited`; for a
   * span-replace override fragment it drops the whole override segment. The
   * host closes the popover after firing this.
   */
  onRevert: () => void;

  /**
   * The reviewer who last applied this change (PLAT-10444) — the phrase's
   * `editedBy`, or its parent override segment's. Drives the "Last edited by …
   * · …" meta line, shown only when editing an existing change. Absent on an
   * unedited phrase / pre-attribution edits ⇒ the line hides.
   */
  editedBy?: { name: string; email?: string; at?: string };

  /**
   * PLAT-10852 (E15) — the glossary entries this fragment was translated from,
   * resolved by the host via `resolveAppliedTerms`. Non-empty ⇒ the read-only
   * "Translation Memory" membership section renders, so the reviewer knows the
   * text is a pinned glossary term before changing it. Omitted / empty ⇒ no
   * section (a fragment with no TM provenance is the common case).
   */
  tmTerms?: AppliedTerm[];

  /**
   * How many TM entry ids the fragment carries. Larger than `tmTerms.length`
   * when the workspace glossary isn't loaded (or an id no longer resolves) —
   * the section then reports membership by count rather than dropping the
   * signal (PLAT-10850 DS5, best-effort).
   */
  tmEntryIdCount?: number;

  /**
   * PLAT-10852 (E15) — "Save to Translation Memory". Receives the value
   * currently in the textarea (the manual translation being promoted), NOT the
   * stored one, so it works before the edit is committed. Promoting is
   * independent of Save: the host opens the entry editor seeded from this, and
   * the popover stays open. Omitted ⇒ no action renders (flag off / no
   * workspace).
   */
  onPromoteToTm?: (value: string) => void;

  /**
   * PLAT-10852 — the fragment ALREADY is a translation-memory entry (backend
   * provenance, or promoted in this session). Takes precedence over
   * `onPromoteToTm`: the row shows a "Translation Memory Entry" link that opens
   * the entry instead of offering to create a second one for the same text.
   */
  onViewTmEntry?: () => void;

  /**
   * PLAT-11335 — the fresh coverage verdict for the phrase being edited, when
   * one exists (the host resolves it from the workspace's coverage cache by
   * stable-id overlap). Renders the coverage badge on the "Your Translation"
   * row, with the verdict's explanation as a hover tooltip — the coverage badge
   * itself only shows in Segment view, so this is the Paragraph/Words reviewer's
   * one look at the score. Absent ⇒ the row renders exactly as before. Note the
   * verdict describes the APPLIED translation, not the in-progress draft.
   */
  coverage?: CoverageVerdict;
}

export const TranslatedPhraseEditorPopover: React.FC<
  TranslatedPhraseEditorPopoverProps
> = ({
  anchorElement,
  sourceText,
  sourceLanguageLabel,
  originalValue,
  currentValue,
  onSave,
  onCancel,
  canRevert,
  onRevert,
  editedBy,
  tmTerms,
  tmEntryIdCount = 0,
  onPromoteToTm,
  onViewTmEntry,
  coverage,
}) => {
  const [value, setValue] = useState(currentValue);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Latest onCancel for the mount-once native keydown trap below.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  // The anchor's rect is read once on open (re-anchoring every frame would
  // fight the user scrolling the transcript while the popover is open); the
  // popover's own height is measured, so it flips above / clamps into the
  // viewport against what actually rendered (PLAT-11479).
  const placement = useAnchoredPopoverPlacement(anchorElement, wrapperRef, {
    width: POPOVER_WIDTH,
    estimatedHeight: POPOVER_ESTIMATED_HEIGHT,
  });

  // Close on click-outside (treat as Cancel — no commit). Escape + Cancel
  // button share the same codepath.
  useOutsideAlerter(wrapperRef, onCancel);

  // PLAT-10644 — opening any other translation popover (composition
  // quick-edit on canvas / transcript) cancels this one.
  useExclusiveTranslationPopover(onCancel);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Focus without selecting. Selecting the whole value made accidental
    // keystrokes destructive — users routinely wanted to tweak a single
    // word, not replace the whole phrase. Caret lands at the end of the
    // value which is the non-destructive default.
    textarea.focus();
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
  }, []);

  // Native-level keydown trap on the wrapper. React's `e.stopPropagation()`
  // on the textarea handler stops React synthetic-event propagation but
  // not native `addEventListener` listeners on ancestor DOM nodes (the
  // transcript scroll container, video keyboard shortcuts on document /
  // window). Without this trap, Space toggles playback, ArrowLeft/Right
  // scrub the video, etc., while the user is typing in the popover.
  //
  // Escape is handled HERE, natively on the wrapper, rather than left to the
  // textarea's React onKeyDown: the transcript scroll container's keydown
  // listener calls stopImmediatePropagation() on every key, so Escape never
  // reaches React's delegation. The wrapper sits BELOW that container, so
  // handling it here fires first — it cancels the edit AND stops the event,
  // so Escape never bubbles to a host (the transcript, or a containing modal
  // such as the Translation Review workspace, whose own Escape-to-close must
  // not fire while an edit is open). Enter still flows to the textarea
  // onKeyDown (Save).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancelRef.current();
        return;
      }
      if (e.key === 'Enter') return;
      e.stopPropagation();
    };
    const stop = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') return;
      e.stopPropagation();
    };
    wrapper.addEventListener('keydown', onKeyDown);
    wrapper.addEventListener('keyup', stop);
    wrapper.addEventListener('keypress', stop);
    return () => {
      wrapper.removeEventListener('keydown', onKeyDown);
      wrapper.removeEventListener('keyup', stop);
      wrapper.removeEventListener('keypress', stop);
    };
  }, []);

  // Membership is "does the fragment carry TM provenance", which the id count
  // answers even when the entries themselves can't be resolved (DS5). Fall back
  // to the resolved terms for hosts that pass only those.
  const tmMembershipCount = Math.max(tmEntryIdCount, tmTerms?.length ?? 0);

  const handleSave = () => onSave(value);

  const handleRevert = () => {
    if (!canRevert) return;
    // Fire the parent revert; the host reverts the store AND closes the
    // popover (revert reflects the applied change, so there's no draft left
    // to compare here).
    onRevert();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Transcript.tsx registers a keydown listener on the scroll
    // container that calls preventDefault() for Backspace, Arrow keys,
    // Space, Enter, Comma, Period, etc., to treat them as transcript-
    // editing shortcuts. This textarea is a DOM descendant of that
    // container, so we stop propagation on every key event to keep the
    // browser's native text-editing behavior inside the input.
    e.stopPropagation();

    // Enter (no shift) commits. Shift+Enter keeps the native newline so
    // multi-line edits remain possible — phrase text can wrap.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  // Portaled to `document.body` (PLAT-11484) — same reason as
  // `ReplaceSpanEditor`: inline, the popover sits inside the transcript scroll
  // container, and in the clip preview (whose modal wrapper's `backdrop-filter`
  // makes `position: fixed` resolve against the modal, not the viewport)
  // Chrome's drag-to-select autoscroll walked up from the textarea to that
  // scroller and scrolled the transcript behind the popover.
  return ReactDOM.createPortal(
    <Wrapper
      ref={wrapperRef}
      placement={placement}
      data-testid="translated-phrase-editor-popover"
      onMouseDown={e => e.stopPropagation()}
      role="dialog"
      aria-label="Edit translation"
    >
      {/* Expanded by default — the source text is the reviewer's reference
          for judging the translation, so it opens revealed (PLAT-10702). */}
      <CollapsibleOriginal
        label={
          sourceLanguageLabel ? `Original · ${sourceLanguageLabel}` : 'Original'
        }
        text={sourceText}
        testId="popover-source-text"
        defaultExpanded
      />

      {/*
        Machine-translation reference + its Revert action live on the same
        row: Revert restores THIS value, so the affordance sits next to
        what it restores rather than in the modal footer (PLAT-10644).
        `canRevert` keeps the section visible even when the draft matches
        the machine value (an applied span-override can still be reverted
        structurally).
      */}
      {originalValue !== undefined &&
        (originalValue !== value || canRevert) && (
          <CollapsibleValueSection
            label="Original Translation"
            testId="popover-original-translation"
            action={
              <InlineTextAction
                type="button"
                onClick={handleRevert}
                disabled={!canRevert}
                data-testid="popover-revert"
              >
                Revert
              </InlineTextAction>
            }
          >
            <OriginalReference data-testid="popover-original-translation">
              {originalValue}
            </OriginalReference>
          </CollapsibleValueSection>
        )}

      {/* PLAT-10852 (E15) — TM membership. Collapsed context, above the
          textarea, so the reviewer sees "this is a pinned glossary term"
          before they type over it. Count-only when the entries can't be
          resolved (DS5). */}
      {tmMembershipCount > 0 && (
        <CollapsibleValueSection
          label={`Translation Memory (${tmMembershipCount})`}
          testId="popover-tm-membership"
        >
          <TmTermList data-testid="popover-tm-terms">
            {tmTerms?.length ? (
              tmTerms.map(term => (
                <TmTermRow key={term.id}>
                  <TmTermPair>
                    {term.sourceTerm}
                    {term.isDnt ? (
                      <TmTermNote>Keep verbatim</TmTermNote>
                    ) : (
                      <>
                        {' → '}
                        {term.translation}
                      </>
                    )}
                  </TmTermPair>
                  <TmTermMeta>{term.collectionName ?? 'Default'}</TmTermMeta>
                </TmTermRow>
              ))
            ) : (
              <TmTermMeta>
                {tmMembershipCount === 1
                  ? '1 translation-memory term'
                  : `${tmMembershipCount} translation-memory terms`}
              </TmTermMeta>
            )}
          </TmTermList>
        </CollapsibleValueSection>
      )}

      <Section>
        <LabelRow>
          <SectionLabel>Your Translation</SectionLabel>
          {coverage && (
            <CoverageBadgeWithTooltip
              verdict={coverage}
              tooltipId="phrase-editor-coverage-tooltip"
            />
          )}
        </LabelRow>
        {coverage &&
          ReactDOM.createPortal(
            <Tooltip
              id="phrase-editor-coverage-tooltip"
              style={COVERAGE_TOOLTIP_STYLE}
              positionStrategy="fixed"
            />,
            document.body,
          )}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          aria-label="Edit translated phrase"
          data-testid="popover-translation-input"
        />
        {/* Already an entry ⇒ a link to it. Otherwise the promote action, which
            pins the CURRENT draft (E15) — independent of Save, so the reviewer
            can pin a correction and still cancel the phrase edit. */}
        {onViewTmEntry ? (
          <PromoteRow>
            <InlineTextAction
              type="button"
              onClick={onViewTmEntry}
              data-testid="popover-view-tm-entry"
            >
              Translation Memory Entry
            </InlineTextAction>
          </PromoteRow>
        ) : (
          onPromoteToTm && (
            <PromoteRow>
              <InlineTextAction
                type="button"
                onClick={() => onPromoteToTm(value)}
                disabled={!value.trim()}
                data-testid="popover-promote-tm"
              >
                Save to Translation Memory
              </InlineTextAction>
            </PromoteRow>
          )
        )}
      </Section>

      {editedBy?.name && (
        <EditedMeta data-testid="popover-edited-meta">
          Last edited by {editedBy.name}
          {formatEditedAt(editedBy.at) && ` · ${formatEditedAt(editedBy.at)}`}
        </EditedMeta>
      )}

      <ButtonRow>
        <Spacer />
        <SecondaryButton
          type="button"
          onClick={onCancel}
          data-testid="popover-cancel"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSave}
          data-testid="popover-save"
        >
          Save
        </PrimaryButton>
      </ButtonRow>
    </Wrapper>,
    document.body,
  );
};

/** Kept in sync with the `Wrapper`'s `width` declaration. */
const POPOVER_WIDTH = 360;
/** First-paint estimate only — the hook re-places against the real height. */
const POPOVER_ESTIMATED_HEIGHT = 320;

const Wrapper = styled.div.attrs(
  (props: { placement: AnchoredPopoverPlacement }) => props,
)`
  position: fixed;
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  width: ${POPOVER_WIDTH}px;
  max-width: calc(100vw - 16px);
  /* Never spill past the viewport: the placement caps the height to the room
     on the side it chose, and the content scrolls inside instead (a long
     Original translation stays in the popover). */
  max-height: ${p => p.placement.maxHeight}px;
  overflow-y: auto;
  /* Body-portaled, so it must clear the clip-preview modal stack on its own;
     inside the Translation Review workspace the lifted context-menu tier
     applies instead (see topLayerZ.ts). */
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );

  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  padding: 14px 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Textarea = styled.textarea`
  background: var(--background);
  color: var(--foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  padding: 8px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;
  min-height: 54px;
  outline: none;

  ${fieldFocusCss}
`;

// PLAT-10852 (E15) — TM membership rows. Read-only context, so they follow the
// `OriginalReference` quote treatment (left rule, muted) rather than looking
// like anything editable.
const TmTermList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TmTermRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0 2px 8px;
  border-left: 2px solid var(--border-elevated);
`;

const TmTermPair = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: var(--popover-foreground);
  word-break: break-word;
  user-select: text;
`;

const TmTermNote = styled.span`
  margin-left: 6px;
  font-size: 11px;
  color: var(--text-muted);
`;

const TmTermMeta = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-muted);
  white-space: nowrap;
`;

// Right-aligned so the quiet promote action reads as a per-value affordance on
// the translation box, not a third footer button competing with Save/Cancel.
const PromoteRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// "Last edited by … · …" attribution line for an existing change (PLAT-10444).
// Quiet meta, matching the section labels' muted weight.
const EditedMeta = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

/** "Your Translation" label with the coverage badge pinned to the right. */
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
